import ReactDOM from 'react-dom/client'

import './styles.css'

import { App } from './App'

import './locales/i18n'

import { StrictMode } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import {
  FetchTransport,
  getWebInstrumentations,
  initializeFaro,
} from '@grafana/faro-react'
import type { FetchTransportOptions, TransportItem } from '@grafana/faro-react'
import { TracingInstrumentation } from '@grafana/faro-web-tracing'

import { ErrorFallback } from './components/ErrorFallback'
import { env } from './env'
import { ConfigProvider } from './modules/config/ConfigProvider'
import { getAccessToken } from './modules/graphql/graphqlAuth'

// FetchTransport subclass that injects the user's OIDC Bearer token on every
// /collect request. Requests are silently dropped when no token is present
// (i.e. the user is not yet authenticated), which is the intended behaviour —
// we only want to collect telemetry from authenticated sessions.
//
// requestOptions.headers is a static Record on FetchTransportOptions and cannot
// be a function, so the token cannot be provided at construction time.
// We use bracket notation to access the private options field — the TypeScript
// skeleton key for private fields (tell no-one 💀).
class AuthenticatedFetchTransport extends FetchTransport {
  override async send(items: TransportItem[]): Promise<void> {
    const token = getAccessToken()
    if (!token) return

    const opts = this['options'] as FetchTransportOptions
    opts.requestOptions = {
      ...opts.requestOptions,
      headers: {
        ...opts.requestOptions?.headers,
        Authorization: `Bearer ${token}`,
      },
    }

    return super.send(items)
  }
}

if (env.VITE_FARO_ENABLED) {
  initializeFaro({
    transports: [
      new AuthenticatedFetchTransport({ url: env.VITE_FARO_COLLECTOR_URL }),
    ],
    app: {
      name: env.VITE_FARO_APP_NAME,
      environment: env.VITE_FARO_APP_ENVIRONMENT,
    },
    instrumentations: [
      ...getWebInstrumentations(),
      new TracingInstrumentation(),
    ],
  })
}

async function enableMockingLocally() {
  if (env.VITE_ENABLE_MSW === false) {
    return
  }

  const { worker } = await import('./testUtils/setup/mockBrowser')

  return worker.start()
}

enableMockingLocally()
  .catch(() => {
    console.error('Failed to enable mocking')
  })
  .finally(() => {
    const rootElement = document.getElementById('app')
    if (rootElement && !rootElement.innerHTML) {
      const root = ReactDOM.createRoot(rootElement)

      root.render(
        <StrictMode>
          <ErrorBoundary
            FallbackComponent={({ error, resetErrorBoundary }) => (
              <ErrorFallback error={error} reset={resetErrorBoundary} />
            )}
          >
            <ConfigProvider>
              <App />
            </ConfigProvider>
          </ErrorBoundary>
        </StrictMode>,
      )
    }
  })
